// DESIGN_AUDIT Phase 11C: hardcoded colors replaced with CSS vars
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Bell, Menu, X, Sun, Moon, Monitor, HelpCircle, MapPin, MessageSquare, Users, LogOut, User, Settings, CreditCard } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext.tsx';
import { useFacility } from '../../contexts/FacilityContext.tsx';
import { useTheme } from '../../contexts/ThemeContext.tsx';
import Chat from '../Chat.tsx';
import BypassCountdown from './BypassCountdown.tsx';
import GlobalSearch from '../search/GlobalSearch.tsx';
import { spinIconOut } from '../../utils/iconAnimations.ts';
import './TopBar.css';
import '../../styles/buttonAnimations.css';

interface TopBarProps {
  onMenuToggle: () => void;
  onMobileToggle?: () => void;
  pageTitle?: string;
}

export default function TopBar({ onMenuToggle, onMobileToggle, pageTitle }: TopBarProps) {
  const { user, logout } = useAuth();
  const { facilityName } = useFacility();
  const { theme, effectiveTheme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showMobileUserMenu, setShowMobileUserMenu] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const themeMenuRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const mobileSearchRef = useRef<HTMLDivElement>(null);
  const mobileThemeMenuRef = useRef<HTMLDivElement>(null);
  const mobileUserMenuRef = useRef<HTMLDivElement>(null);

  // Escape key closes dropdowns (GlobalSearch handles its own Escape behavior internally --
  // this only needs to collapse the mobile search bar back to icons).
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowMobileUserMenu(false);
        setSearchExpanded(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close dropdowns on outside click (GlobalSearch handles closing its own dropdown)
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const inMobileSearch = mobileSearchRef.current?.contains(e.target as Node);
      if (!inMobileSearch && searchExpanded) {
        setSearchExpanded(false);
      }

      const inDesktopTheme = themeMenuRef.current?.contains(e.target as Node);
      const inMobileTheme = mobileThemeMenuRef.current?.contains(e.target as Node);
      if (!inDesktopTheme && !inMobileTheme) setShowThemeMenu(false);

      if (mobileUserMenuRef.current && !mobileUserMenuRef.current.contains(e.target as Node)) {
        setShowMobileUserMenu(false);
      }

      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [searchExpanded]);

  // Listen for openChat / openDirectMessage events
  useEffect(() => {
    const handler = () => setChatOpen(true);
    window.addEventListener('openChat', handler);
    window.addEventListener('openDirectMessage', handler);
    return () => {
      window.removeEventListener('openChat', handler);
      window.removeEventListener('openDirectMessage', handler);
    };
  }, []);

  const ThemeIcon = effectiveTheme === 'dark' ? Moon : Sun;
  const userInitial = (user?.firstName?.[0] ?? user?.username?.[0] ?? 'U').toUpperCase();

  return (
    <>
    <header className="topbar">

      {/* ── Mobile-only compact header ──────────────────────────────────────── */}
      <div className="mobile-header">
        {/* Hamburger — integrated into the mobile header */}
        <button
          className="mobile-header-hamburger"
          onClick={onMobileToggle}
          aria-label="Toggle navigation"
        >
          <Menu size={20} color="#f9fafb" />
        </button>

        {/* Facility name — center */}
        <span className="mobile-header-facility">
          {facilityName || 'ProQure'}
        </span>

        {/* Bypass countdown (v143) — renders nothing when no bypass is active */}
        <BypassCountdown />

        {/* Avatar button — opens user dropdown */}
        <div className="mobile-header-avatar-wrap" ref={mobileUserMenuRef}>
          <button
            className="mobile-header-avatar-btn"
            onClick={() => setShowMobileUserMenu((s) => !s)}
            aria-label="User menu"
          >
            <div className="topbar-user-avatar">{userInitial}</div>
          </button>

          {showMobileUserMenu && (
            <div className="mobile-user-menu">
              <div className="mobile-user-menu-name">
                <User size={13} style={{ flexShrink: 0 }} />
                {user?.firstName ?? user?.username}
              </div>
              <button
                className="mobile-user-menu-logout"
                onClick={() => { logout(); setShowMobileUserMenu(false); }}
              >
                <LogOut size={13} style={{ flexShrink: 0 }} />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Desktop elements (hidden on mobile via CSS) ──────────────────── */}
      <div className="topbar-left topbar-desktop-only">
        <button className="topbar-menu-btn" onClick={onMenuToggle}>
          <Menu size={20} />
        </button>
        {pageTitle && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h1 className="topbar-title">{pageTitle}</h1>
            {facilityName && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '2px 8px', borderRadius: 999,
                fontSize: '0.7rem', fontWeight: 500,
                background: 'rgba(59,130,246,0.12)',
                color: 'var(--accent-primary)',
                border: '1px solid rgba(59,130,246,0.2)',
                whiteSpace: 'nowrap',
              }}>
                <MapPin size={10} />
                {facilityName}
              </span>
            )}
          </div>
        )}
        {/* Bypass countdown (v143) — deliberately OUTSIDE the `pageTitle &&` block above so it
            stays visible on every page, including ones that don't pass a pageTitle. Renders
            nothing when the user has no active bypass. */}
        <BypassCountdown />
      </div>

      <div className="topbar-center topbar-desktop-only">
        <GlobalSearch />
      </div>

      <div className="topbar-right topbar-desktop-only">
        {/* Theme toggle */}
        <div ref={themeMenuRef} style={{ position: 'relative' }}>
          <button
            className="topbar-icon-btn"
            title={`Theme: ${theme}`}
            onClick={() => setShowThemeMenu((s) => !s)}
            onMouseLeave={spinIconOut}
          >
            <ThemeIcon size={17} className="btn-icon" />
          </button>
          {showThemeMenu && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 400,
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-md)',
              minWidth: 160, padding: 4,
            }}>
              {([
                { id: 'dark', label: 'Dark', icon: <Moon size={14} /> },
                { id: 'light', label: 'Light', icon: <Sun size={14} /> },
                { id: 'system', label: 'System', icon: <Monitor size={14} /> },
              ] as const).map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => { setTheme(opt.id); setShowThemeMenu(false); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    width: '100%', padding: '8px 12px',
                    background: theme === opt.id ? 'rgba(59,130,246,0.12)' : 'transparent',
                    border: 'none', borderRadius: 'var(--radius-sm)',
                    color: theme === opt.id ? 'var(--accent-primary)' : 'var(--text-secondary)',
                    cursor: 'pointer', fontSize: '0.875rem', textAlign: 'left',
                    fontWeight: theme === opt.id ? 600 : 400,
                  }}
                >
                  {opt.icon} {opt.label}
                  {theme === opt.id && <span style={{ marginLeft: 'auto', fontSize: '0.7rem' }}>✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Notifications */}
        <button className="topbar-icon-btn" title="Notifications" onMouseLeave={spinIconOut}>
          <Bell size={18} className="btn-icon" />
        </button>

        {/* Team Chat */}
        <div style={{ position: 'relative' }}>
          <button
            className="topbar-icon-btn"
            title="Team Chat"
            onClick={() => setChatOpen((o) => !o)}
            onMouseLeave={spinIconOut}
            style={chatOpen ? {
              background: 'rgba(99,102,241,0.15)',
              border: '1px solid rgba(99,102,241,0.3)',
              color: '#818cf8',
            } : undefined}
          >
            <MessageSquare size={18} className="btn-icon" />
          </button>
          {unreadCount > 0 && (
            <div style={{
              position: 'absolute', top: -4, right: -4,
              background: 'var(--accent-danger)', color: '#fff',
              fontSize: 11, fontWeight: 700,
              borderRadius: 999, padding: '1px 5px', minWidth: 18,
              textAlign: 'center', lineHeight: '16px',
              border: '1.5px solid var(--bg-base)',
              pointerEvents: 'none',
            }}>
              {unreadCount > 99 ? '99+' : unreadCount}
            </div>
          )}
        </div>

        {/* Users */}
        <button
          className="topbar-icon-btn"
          title="Team Members"
          onClick={() => navigate('/users')}
          onMouseLeave={spinIconOut}
        >
          <Users size={18} className="btn-icon" />
        </button>

        {/* Help */}
        <button
          className="topbar-icon-btn"
          title="Help"
          onClick={() => navigate('/help')}
          onMouseLeave={spinIconOut}
        >
          <HelpCircle size={18} className="btn-icon" />
        </button>

        <div ref={userMenuRef} style={{ position: 'relative' }}>
          <button
            className="topbar-user-chip"
            onClick={() => setShowUserMenu((s) => !s)}
            style={{ border: 'none', cursor: 'pointer' }}
          >
            <div className="topbar-user-avatar">
              {userInitial}
            </div>
            <span className="topbar-user-name">
              {user?.firstName ?? user?.username}
            </span>
          </button>
          {showUserMenu && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 400,
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-md)',
              minWidth: 180, padding: 4,
            }}>
              <div style={{
                padding: '8px 12px 6px',
                fontSize: '0.78rem', color: 'var(--text-muted)',
                borderBottom: '1px solid var(--border-default)', marginBottom: 4,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {user?.email ?? user?.username}
              </div>
              {/* Billing — manager/org_admin/admin only, same role-gate pattern as Sidebar's
                  managerOnly nav items and App.tsx's requiredRoles guard on /billing. */}
              {(user?.role === 'manager' || user?.role === 'org_admin' || user?.role === 'admin') && (
                <button
                  onClick={() => { navigate('/billing'); setShowUserMenu(false); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    width: '100%', padding: '8px 12px',
                    background: 'transparent',
                    border: 'none', borderRadius: 'var(--radius-sm)',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer', fontSize: '0.875rem', textAlign: 'left',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <CreditCard size={14} /> Billing
                </button>
              )}
              <button
                onClick={() => { navigate('/settings'); setShowUserMenu(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  width: '100%', padding: '8px 12px',
                  background: 'transparent',
                  border: 'none', borderRadius: 'var(--radius-sm)',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer', fontSize: '0.875rem', textAlign: 'left',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <Settings size={14} /> Settings
              </button>
              <button
                onClick={() => { logout(); setShowUserMenu(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  width: '100%', padding: '8px 12px',
                  background: 'transparent',
                  border: 'none', borderRadius: 'var(--radius-sm)',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer', fontSize: '0.875rem', textAlign: 'left',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <LogOut size={14} /> Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Chat slide-in panel */}
      <Chat
        isOpen={chatOpen}
        onClose={() => setChatOpen(false)}
        facilityName={facilityName || undefined}
        onUnreadCountChange={(n) => setUnreadCount(n)}
      />
    </header>

    {/* ── Mobile second toolbar row — separate fixed element at top:52px ── */}
    <div className="mobile-toolbar-row">
      {searchExpanded ? (
        /* ── Expanded: full-width search input ── */
        <div className="mobile-toolbar-search-expanded" ref={mobileSearchRef}>
          <GlobalSearch autoFocus placeholder="Search..." />
          <button
            className="topbar-icon-btn"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setSearchExpanded(false)}
            aria-label="Close search"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        /* ── Collapsed: 6 icon buttons evenly spaced ── */
        <div className="mobile-toolbar-icons">
          <button
            className="topbar-icon-btn"
            title="Search"
            onClick={() => setSearchExpanded(true)}
          >
            <Search size={17} />
          </button>

          <button
            className="topbar-icon-btn"
            title="Team Members"
            onClick={() => navigate('/users')}
            onMouseLeave={spinIconOut}
          >
            <Users size={17} className="btn-icon" />
          </button>

          <button
            className="topbar-icon-btn"
            title="Team Chat"
            onClick={() => setChatOpen((o) => !o)}
            onMouseLeave={spinIconOut}
            style={chatOpen ? { background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)', color: '#818cf8' } : undefined}
          >
            <MessageSquare size={17} className="btn-icon" />
          </button>

          <button className="topbar-icon-btn" title="Notifications" onMouseLeave={spinIconOut}>
            <Bell size={17} className="btn-icon" />
          </button>

          <div ref={mobileThemeMenuRef} style={{ position: 'relative' }}>
            <button
              className="topbar-icon-btn"
              title={`Theme: ${theme}`}
              onClick={() => setShowThemeMenu((s) => !s)}
              onMouseLeave={spinIconOut}
            >
              <ThemeIcon size={17} className="btn-icon" />
            </button>
            {showThemeMenu && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 1200,
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--radius-md)',
                boxShadow: 'var(--shadow-md)',
                minWidth: 150, padding: 4,
              }}>
                {([
                  { id: 'dark', label: 'Dark', icon: <Moon size={14} /> },
                  { id: 'light', label: 'Light', icon: <Sun size={14} /> },
                  { id: 'system', label: 'System', icon: <Monitor size={14} /> },
                ] as const).map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => { setTheme(opt.id); setShowThemeMenu(false); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      width: '100%', padding: '8px 12px',
                      background: theme === opt.id ? 'rgba(59,130,246,0.12)' : 'transparent',
                      border: 'none', borderRadius: 'var(--radius-sm)',
                      color: theme === opt.id ? 'var(--accent-primary)' : 'var(--text-secondary)',
                      cursor: 'pointer', fontSize: '0.875rem', textAlign: 'left',
                      fontWeight: theme === opt.id ? 600 : 400,
                    }}
                  >
                    {opt.icon} {opt.label}
                    {theme === opt.id && <span style={{ marginLeft: 'auto', fontSize: '0.7rem' }}>✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            className="topbar-icon-btn"
            title="Help"
            onClick={() => navigate('/help')}
            onMouseLeave={spinIconOut}
          >
            <HelpCircle size={17} className="btn-icon" />
          </button>
        </div>
      )}
    </div>
    </>
  );
}
